import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Code,
  Group,
  Modal,
  NumberInput,
  Stack,
  Table,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCertificate,
  IconCheck,
  IconCopy,
  IconEdit,
  IconPlus,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import {
  useCreateApiClient,
  useIntegrationApiClient,
  usePatchApiClientScopes,
  useRegisterApiClientCertificate,
  useRevokeApiClientCertificate,
  useRevokeApiClientSecret,
  useRotateApiClientSecret,
  type ApiClientCertificateView,
  type ApiClientSecretView,
  type CreatedApiClient,
  type RotatedSecret,
} from '../api/api-clients';

interface Props {
  orgId: string;
  productId: string;
  /** Distinct scopes from the live endpoint catalogue, for the autocomplete on provisioning. */
  catalogueScopes: string[];
  productDisplayName: string;
}

/**
 * Credentials section on the integration page. Shows the
 * OAuth2 client_id, lists active + pending-expiry secrets, and
 * offers two destructive actions:
 *   - Rotate — mints a new secret, optionally keeping the old one
 *     valid for a configurable overlap window (default 7 days) so
 *     the consumer can roll over without an outage.
 *   - Revoke — deactivates a specific secret immediately.
 *
 * Plaintext secrets are only ever shown once, in the modal that
 * appears after a successful rotation. They are never re-fetchable.
 */
export function CredentialsCard({ orgId, productId, catalogueScopes, productDisplayName }: Props) {
  const query = useIntegrationApiClient(orgId, productId);
  const rotate = useRotateApiClientSecret(orgId, productId);
  const revoke = useRevokeApiClientSecret(orgId, productId);
  const patchScopes = usePatchApiClientScopes(orgId, productId);

  const [rotateOpen, setRotateOpen] = useState(false);
  const [overlapDays, setOverlapDays] = useState<number>(7);
  const [mintedSecret, setMintedSecret] = useState<RotatedSecret | null>(null);
  const [provisioned, setProvisioned] = useState<CreatedApiClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editScopesOpen, setEditScopesOpen] = useState(false);
  const [editedScopes, setEditedScopes] = useState<string[]>([]);

  if (query.isPending) {
    return (
      <Card withBorder padding="lg">
        <Title order={5}>OAuth2 credentials</Title>
        <Text size="sm" c="dimmed">
          Loading…
        </Text>
      </Card>
    );
  }
  const client = query.data;
  if (!client) {
    return (
      <>
        <ProvisionCard
          orgId={orgId}
          productId={productId}
          productDisplayName={productDisplayName}
          catalogueScopes={catalogueScopes}
          onProvisioned={setProvisioned}
        />
        <SecretRevealModal
          minted={provisioned}
          onClose={() => setProvisioned(null)}
          title="API client created"
          description="Copy this client_secret now — we never show it again. Combine it with the client_id below to obtain access tokens."
        />
      </>
    );
  }

  function handleRotate() {
    setError(null);
    rotate.mutate(
      { overlap_days: overlapDays },
      {
        onSuccess: (data) => {
          setMintedSecret(data);
          setRotateOpen(false);
        },
        onError: () => setError('Could not rotate the secret. Try again.'),
      },
    );
  }

  function handleRevoke(secretId: string) {
    setError(null);
    revoke.mutate(secretId, {
      onError: () => setError('Could not revoke the secret. Try again.'),
    });
  }

  return (
    <>
      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <Title order={5}>OAuth2 credentials</Title>
            <Group gap="xs">
              <Button
                variant="default"
                size="xs"
                leftSection={<IconEdit size={14} />}
                onClick={() => {
                  setEditedScopes(client.scopes);
                  setEditScopesOpen(true);
                }}
              >
                Edit scopes
              </Button>
              <Button
                variant="default"
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={() => setRotateOpen(true)}
              >
                Rotate secret
              </Button>
            </Group>
          </Group>
          <Text size="sm" c="dimmed">
            Use the <Code>client_id</Code> below + the latest <Code>client_secret</Code> against the
            auth service's <Code>/oauth/token</Code> endpoint to obtain an access token.
          </Text>

          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {error}
            </Alert>
          )}

          <Group gap="md" wrap="wrap">
            <KeyValue label="Client id" value={client.id} copyable />
            <KeyValue label="Display name" value={client.displayName} />
            <KeyValue label="Status" value={client.status} />
          </Group>

          <div>
            <Text size="xs" c="dimmed" mb={4}>
              Granted scopes
            </Text>
            <Group gap="xs" wrap="wrap">
              {client.scopes.map((s) => (
                <Badge key={s} variant="light" color="blue">
                  {s}
                </Badge>
              ))}
            </Group>
          </div>

          <Title order={6} mt="sm">
            Secrets
          </Title>
          {client.secrets.length === 0 ? (
            <Text size="sm" c="dimmed">
              No secrets — rotate to mint the first one.
            </Text>
          ) : (
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Label</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Expires</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {client.secrets.map((s) => (
                  <Table.Tr key={s.id}>
                    <Table.Td>{s.label ?? '—'}</Table.Td>
                    <Table.Td>{renderStatus(s)}</Table.Td>
                    <Table.Td>{new Date(s.createdAt).toLocaleString()}</Table.Td>
                    <Table.Td>
                      {s.expiresAt ? new Date(s.expiresAt).toLocaleString() : '—'}
                    </Table.Td>
                    <Table.Td>
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-xs"
                        leftSection={<IconTrash size={12} />}
                        onClick={() => handleRevoke(s.id)}
                        disabled={revoke.isPending || s.status === 'revoked'}
                      >
                        Revoke now
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>

      <CertificatesCard orgId={orgId} productId={productId} certificates={client.certificates} />

      <Modal
        opened={editScopesOpen}
        onClose={() => {
          patchScopes.reset();
          setEditScopesOpen(false);
        }}
        title="Edit granted scopes"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Update the scopes this OAuth2 client is permitted to request. Changes take effect on the
            next token mint — existing tokens remain valid until they expire.
          </Text>
          <TagsInput
            label="Scopes"
            description="Pick from the catalogue or type a wildcard like `cases:*`. Press Enter to add."
            data={catalogueScopes}
            value={editedScopes}
            onChange={setEditedScopes}
            placeholder="cases:read, cases:*, …"
            clearable
          />
          {patchScopes.isError && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              Could not update scopes. Try again.
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                patchScopes.reset();
                setEditScopesOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                patchScopes.mutate(editedScopes, { onSuccess: () => setEditScopesOpen(false) })
              }
              loading={patchScopes.isPending}
              disabled={editedScopes.length === 0}
            >
              Save scopes
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={rotateOpen}
        onClose={() => setRotateOpen(false)}
        title="Rotate client secret"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            A new <Code>client_secret</Code> will be minted. To avoid downtime, the previous secret
            stays valid for the overlap window below — long enough to swap into your deployment,
            short enough that a leaked secret has a hard upper bound.
          </Text>
          <NumberInput
            label="Overlap window (days the old secret stays valid)"
            description="Set to 0 to revoke the previous secret immediately."
            value={overlapDays}
            onChange={(v) => setOverlapDays(typeof v === 'number' ? v : 7)}
            min={0}
            max={90}
          />
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            The plaintext secret is shown only once after rotation. Copy it immediately.
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setRotateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRotate} loading={rotate.isPending}>
              Mint new secret
            </Button>
          </Group>
        </Stack>
      </Modal>

      <SecretRevealModal
        minted={mintedSecret}
        onClose={() => setMintedSecret(null)}
        title="New client secret"
        description="Copy this now — we never show it again. The previous secret stays valid until its expiry shown in the table."
      />
    </>
  );
}

interface ProvisionCardProps {
  orgId: string;
  productId: string;
  productDisplayName: string;
  catalogueScopes: string[];
  onProvisioned: (created: CreatedApiClient) => void;
}

/**
 * Replaces the legacy "no client provisioned" message with a button
 * that opens a modal to create the OAuth2 client. The modal asks for
 * a display name + a scope list (autocompleted from the live
 * endpoint catalogue, free-text accepted for wildcards like
 * `cases:*`). On success a one-shot reveal modal shows the
 * plaintext secret; the rest of the page refreshes via the existing
 * `useIntegrationApiClient` query invalidation.
 */
function ProvisionCard({
  orgId,
  productId,
  productDisplayName,
  catalogueScopes,
  onProvisioned,
}: ProvisionCardProps) {
  const create = useCreateApiClient(orgId, productId);
  const [open, setOpen] = useState(false);
  const [displayName, setDisplayName] = useState(`${productDisplayName} integration`);
  const [scopes, setScopes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    create.mutate(
      { display_name: displayName.trim(), scopes },
      {
        onSuccess: (data) => {
          onProvisioned(data);
          setOpen(false);
          setScopes([]);
        },
        onError: () => setError('Could not provision the API client. Try again.'),
      },
    );
  }

  return (
    <>
      <Card withBorder padding="lg">
        <Stack gap="sm">
          <Title order={5}>OAuth2 credentials</Title>
          <Text size="sm" c="dimmed">
            No API client has been provisioned for this (organisation, product) yet. Provision one
            below to mint a <Code>client_id</Code> + <Code>client_secret</Code> the consumer can use
            against <Code>/oauth/token</Code>.
          </Text>
          <Group>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setOpen(true)}>
              Provision API client
            </Button>
          </Group>
        </Stack>
      </Card>

      <Modal opened={open} onClose={() => setOpen(false)} title="Provision API client" size="lg">
        <Stack gap="md">
          <Text size="sm">
            A new OAuth2 client_credentials grant for this (organisation, product). The plaintext
            secret is shown once after creation — copy it into the consumer's deploy.
          </Text>
          <TextInput
            label="Display name"
            description="What appears in audit logs + the admin clients list."
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
            required
          />
          <TagsInput
            label="Scopes"
            description="Pick from the catalogue or type a wildcard like `cases:*`. Press Enter to add."
            data={catalogueScopes}
            value={scopes}
            onChange={setScopes}
            placeholder="cases:read, cases:*, …"
            clearable
          />
          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              {error}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              loading={create.isPending}
              disabled={!displayName.trim() || scopes.length === 0}
            >
              Provision
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

interface SecretRevealModalProps {
  minted: { client_secret: string } | null;
  onClose: () => void;
  title: string;
  description: string;
}

/**
 * Shared one-shot reveal of a plaintext secret. Used by both
 * provisioning and rotation flows — same UI on both so operators
 * recognise the "copy now or lose it" moment.
 */
function SecretRevealModal({ minted, onClose, title, description }: SecretRevealModalProps) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal
      opened={!!minted}
      onClose={() => {
        setCopied(false);
        onClose();
      }}
      title={title}
      size="lg"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
          {description}
        </Alert>
        <Box>
          <Text size="xs" c="dimmed" mb={4}>
            client_secret
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Code style={{ flex: 1, wordBreak: 'break-all' }}>{minted?.client_secret ?? ''}</Code>
            <Button
              variant="default"
              size="xs"
              leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              onClick={async () => {
                if (minted) {
                  await navigator.clipboard.writeText(minted.client_secret);
                  setCopied(true);
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Group>
        </Box>
        <Group justify="flex-end">
          <Button
            onClick={() => {
              setCopied(false);
              onClose();
            }}
          >
            I've copied the secret
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function KeyValue({
  label,
  value,
  copyable = false,
}: {
  label: string;
  value: string;
  copyable?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Group gap={4} wrap="nowrap">
        <Code>{value}</Code>
        {copyable && (
          <Button
            variant="subtle"
            size="compact-xs"
            onClick={async () => {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        )}
      </Group>
    </div>
  );
}

function renderStatus(s: ApiClientSecretView): React.ReactNode {
  if (s.status === 'revoked') {
    return (
      <Badge color="red" variant="light">
        revoked
      </Badge>
    );
  }
  if (s.expiresAt) {
    const expires = new Date(s.expiresAt).getTime();
    const expired = expires < Date.now();
    return (
      <Badge color={expired ? 'red' : 'yellow'} variant="light">
        {expired ? 'expired' : 'rolling over'}
      </Badge>
    );
  }
  return (
    <Badge color="green" variant="light">
      active
    </Badge>
  );
}

interface CertificatesCardProps {
  orgId: string;
  productId: string;
  certificates: ApiClientCertificateView[];
}

/**
 * Certificates section under the OAuth2 credentials card. Lets an
 * operator upload an X.509 PEM the consumer will present as their
 * mTLS client cert (RFC 8705). Once at least one active cert is on
 * file the auth service requires a cert on every token request from
 * this client — uploading is an opt-in to the stronger client-auth
 * mode.
 */
function CertificatesCard({ orgId, productId, certificates }: CertificatesCardProps) {
  const register = useRegisterApiClientCertificate(orgId, productId);
  const revoke = useRevokeApiClientCertificate(orgId, productId);
  const [open, setOpen] = useState(false);
  const [pem, setPem] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    register.mutate(
      { pem, label: label.trim() ? label.trim() : undefined },
      {
        onSuccess: () => {
          setOpen(false);
          setPem('');
          setLabel('');
        },
        onError: (err) => setError(extractError(err)),
      },
    );
  }

  const hasActive = certificates.some((c) => c.status === 'active');

  return (
    <>
      <Card withBorder padding="lg" mt="md">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <Title order={5}>Client certificates (mTLS)</Title>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconCertificate size={14} />}
              onClick={() => setOpen(true)}
            >
              Add certificate
            </Button>
          </Group>
          <Text size="sm" c="dimmed">
            Optional. Upload an X.509 PEM the consumer will present on the TLS handshake. While at
            least one active certificate is on file, this client must present a matching cert on
            every token request — issued tokens carry an RFC 8705 <Code>cnf.x5t#S256</Code> binding
            so resource servers can verify the same cert is in play.
          </Text>
          {!hasActive && certificates.length === 0 && (
            <Text size="sm" c="dimmed">
              No certificates registered — this client authenticates by <Code>client_secret</Code>{' '}
              only.
            </Text>
          )}
          {certificates.length > 0 && (
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Label</Table.Th>
                  <Table.Th>Subject</Table.Th>
                  <Table.Th>Fingerprint</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Expires</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {certificates.map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>{c.label ?? '—'}</Table.Td>
                    <Table.Td>
                      <Text size="xs">{c.subject}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Code style={{ fontSize: 11 }}>{shortFingerprint(c.fingerprintSha256)}</Code>
                    </Table.Td>
                    <Table.Td>{renderCertStatus(c)}</Table.Td>
                    <Table.Td>{new Date(c.notAfter).toLocaleDateString()}</Table.Td>
                    <Table.Td>
                      <Button
                        variant="subtle"
                        color="red"
                        size="compact-xs"
                        leftSection={<IconTrash size={12} />}
                        onClick={() => revoke.mutate(c.id)}
                        disabled={revoke.isPending || c.status === 'revoked'}
                      >
                        Revoke
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Card>

      <Modal opened={open} onClose={() => setOpen(false)} title="Add client certificate" size="lg">
        <Stack gap="md">
          <Text size="sm">
            Paste the consumer's PEM-encoded X.509 certificate below. Validity dates are read from
            the cert; expired certs are rejected on upload. We store the full PEM so we can rebuild
            the trust store without re-uploading, and the SHA-256 fingerprint as the primary lookup
            key.
          </Text>
          <TextInput
            label="Label (optional)"
            placeholder="e.g. acme-corp-2026"
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
          />
          <Textarea
            label="PEM certificate"
            placeholder="-----BEGIN CERTIFICATE-----&#10;..."
            value={pem}
            onChange={(e) => setPem(e.currentTarget.value)}
            minRows={10}
            autosize
            styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              {error}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              loading={register.isPending}
              disabled={!pem.trim().startsWith('-----BEGIN CERTIFICATE-----')}
            >
              Register
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function shortFingerprint(fp: string): string {
  const hex = fp.replace(/:/g, '');
  return `${hex.slice(0, 8)}…${hex.slice(-8)}`;
}

function renderCertStatus(c: ApiClientCertificateView): React.ReactNode {
  if (c.status === 'revoked') {
    return (
      <Badge color="red" variant="light">
        revoked
      </Badge>
    );
  }
  const expired = new Date(c.notAfter).getTime() < Date.now();
  if (expired) {
    return (
      <Badge color="red" variant="light">
        expired
      </Badge>
    );
  }
  return (
    <Badge color="green" variant="light">
      active
    </Badge>
  );
}

function extractError(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as { message: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return 'Could not register the certificate. Check the PEM is valid + not expired.';
}
